import React from "react";
import { useForm } from "react-hook-form";

import { trpc } from "@blis/trpc/react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogBody,
  DialogCloseButton,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/field";
import { Form } from "@/components/ui/form";
import { Modal } from "@/components/ui/modal";
import { Separator } from "@/components/ui/separator";
import type { TAccession } from "@/lib/types";
import { mutationResponse } from "@/utils/trpc";
import { useDialogState } from "../detail/state";
import { BillToSelect } from "../form-new/components/BillToSelect";
import { EmailSelect } from "../form-new/components/EmailSelect";
import { PrimaryClientSelect } from "../form-new/components/PrimaryClientSelect";
import { SharedOrgSelect } from "../form-new/components/SharedOrgSelect";
import { SplitBillingSelect } from "../form-new/components/SplitBillingSelect";

type UpdateOrgsDialogProps = {
  accession: TAccession;
};

type UpdateOrgsForm = {
  primaryClient: number;
  billToOrgId: number | null;
  sharedOrgs: number[];
  additionalEmails: string[];
  splitBillingOrgs: number[];
};

export function UpdateOrgsDialog({ accession }: UpdateOrgsDialogProps) {
  const [isOpen, setIsOpen] = useDialogState("editClient");

  const { mutate: updateOrgs, isPending } =
    trpc.accession.updateClient.useMutation(
      mutationResponse({
        errorTitle: "Failed to update clients",
        successMessage: "Clients updated",
        onSuccess: () => {
          setIsOpen(false);
        },
      }),
    );

  const methods = useForm<UpdateOrgsForm>({
    defaultValues: {
      primaryClient: accession.orgId,
      billToOrgId: accession.billToOrgId,
      sharedOrgs: accession.additionalOrgs.map((o) => o.orgId),
      additionalEmails: accession.additionalEmails ?? [],
      splitBillingOrgs: accession.splitBillings.map((o) => o.orgId),
    },
  });

  const { handleSubmit, watch, setValue } = methods;

  const onSubmit = (value: UpdateOrgsForm) => {
    updateOrgs({
      accessionId: accession.id,
      orgId: value.primaryClient,
      billToOrgId: value.billToOrgId,
      additionalEmails: value.additionalEmails,
      secondaryOrgs: value.sharedOrgs,
      splitBillingOrgs: value.splitBillingOrgs,
    });
  };

  const autoFocusInput = React.useCallback((node: HTMLInputElement | null) => {
    setTimeout(() => {
      node?.focus();
    }, 0);
  }, []);

  const primaryClient = watch("primaryClient");

  return (
    <Modal isOpen={isOpen} onOpenChange={setIsOpen}>
      <Dialog>
        <DialogHeader>
          <DialogTitle>Edit clients</DialogTitle>
        </DialogHeader>
        <DialogBody className="gap-4">
          <Form
            id="update-orgs-form"
            className="contents"
            onSubmit={(e) => {
              e.preventDefault();
              handleSubmit(onSubmit)(e);
            }}
          >
            <div className="flex items-center justify-between">
              <Label>Primary client</Label>
              <PrimaryClientSelect
                value={primaryClient}
                onChange={(val) => setValue("primaryClient", val)}
                inputRef={autoFocusInput}
              />
            </div>
            <Separator />
            <div className="flex items-center justify-between">
              <Label>Bill to</Label>
              <BillToSelect
                value={watch("billToOrgId") ?? undefined}
                onChange={(value) => {
                  setValue("billToOrgId", value ?? null);
                }}
                orgId={primaryClient}
              />
            </div>
            <Separator />
            <div className="flex flex-col gap-2">
              <div className="flex items-end justify-between">
                <Label>Share reports</Label>
                <div className="flex items-center gap-2">
                  <SharedOrgSelect
                    value={watch("sharedOrgs")}
                    onChange={(val) => setValue("sharedOrgs", val)}
                    orgId={primaryClient}
                    inputRef={autoFocusInput}
                  />
                </div>
              </div>
            </div>
            <Separator />
            <div className="flex flex-col gap-2">
              <div className="flex items-center justify-between">
                <Label>Email reports</Label>
                <div className="flex items-center gap-2">
                  <EmailSelect
                    value={watch("additionalEmails")}
                    onValueChange={(val) => setValue("additionalEmails", val)}
                    inputRef={autoFocusInput}
                  />
                </div>
              </div>
            </div>
            <Separator />
            <div className="flex flex-col gap-2">
              <div className="flex items-center justify-between">
                <Label>Split billing</Label>
                <SplitBillingSelect
                  value={watch("splitBillingOrgs")}
                  onChange={(val) => setValue("splitBillingOrgs", val)}
                  orgId={primaryClient}
                />
              </div>
            </div>
            <Separator />
          </Form>
        </DialogBody>
        <DialogFooter>
          <Button
            type="submit"
            form="update-orgs-form"
            isPending={isPending}
            isDisabled={isPending}
          >
            Save
          </Button>
          <DialogCloseButton>Cancel</DialogCloseButton>
        </DialogFooter>
      </Dialog>
    </Modal>
  );
}
